import { ROW_UNIQUE_KEY_NAME } from '../../../common/utils';
import { AlgorithmSourceFormDataSchema } from './schema/AlgorithmSourceFormDataSchema';
import { DataObjectFormDataType } from '../../types';
import { filterOption, FormSelect } from '../../../common/form';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { defaultSourceFormData } from './AlgorithmSourcesFormModal';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { AlgorithmSourcesValidFormDataType } from './schema/types';

export interface AlgorithmSourceInSourceSelectProps {
  name: string;
  index: number;
  defaultDataObjectValues: DataObjectFormDataType;
}

export const AlgorithmSourceInSourceSelect: FC<
  AlgorithmSourceInSourceSelectProps
> = (props) => {
  const {
    name,
    index,
    defaultDataObjectValues: { dataObjectId },
  } = props;

  const { getValues, setValue } = useFormContext();
  const sources = useWatch<AlgorithmSourcesValidFormDataType, 'sources'>({
    name: 'sources' as const,
  });
  const selectedType = useMemo(
    () => sources.find(({ sourceType }) => sourceType)?.sourceType,
    [sources],
  );

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const options = useMemo(() => {
    const opt: DefaultOptionType[] = [];
    if (!selectedType || selectedType === 'IN') {
      const id = dataObjectId || 0;
      opt.push({
        value: 'dod-' + id,
        label:
          dataObjectDescriptionVersion.code +
          ' - ' +
          dataObjectDescriptionVersion.name,
        data: {
          dataObjectDescription: {
            id,
            name: dataObjectDescriptionVersion.name,
            code: dataObjectDescriptionVersion.code,
            dataObjectType: 'IN',
            outputTypeCode: null,
          },
          dimensions: dataObjectDescriptionVersion.dimensions,
        },
      });
    }
    if (!selectedType || selectedType === 'DB') {
      const dbDataSources = (
        dataObjectDescriptionVersion.dataSources || []
      ).filter((item) => item.sourceType === 'DATABASE');
      dbDataSources.forEach((item) => {
        const id = item.id || 0;
        opt.push({
          type: 'DB',
          value: 'ds-' + item.id,
          label: item.code + ' - ' + item.name,
          data: {
            dataSource: { ...item, id },
          },
        });
      });
    }
    return opt;
  }, [selectedType, dataObjectDescriptionVersion, dataObjectId]);

  const handleChange = useCallback(
    (value: number, option: any) => {
      if (option?.data) {
        setValue(
          `sources.${index}`,
          AlgorithmSourceFormDataSchema.parse({
            ...getValues(`sources.${index}`),
            ...option.data,
          }),
          { shouldDirty: true },
        );
      } else {
        const {
          [ROW_UNIQUE_KEY_NAME]: key,
          alias,
          numberOfPreviousPeriod,
        } = getValues(`sources.${index}`);

        setValue(
          `sources.${index}`,
          {
            ...defaultSourceFormData,
            [ROW_UNIQUE_KEY_NAME]: key,
            alias,
            numberOfPreviousPeriod,
          },
          { shouldDirty: true },
        );
      }
    },
    [getValues, setValue],
  );

  return (
    <FormSelect
      allowClear
      showSearch
      name={name}
      options={options}
      onChange={handleChange}
      filterOption={filterOption}
    />
  );
};
